import React, { useState, useEffect, useContext } from 'react'
import { Table, Row, Col, Form } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faEraser, faLock, faArrowRotateRight } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { useAuth } from '../../modules/auth'
import roleContext from '../../context/roleContext'
import { RoleContextValue } from '../../types/OtherTypes'
import { getDate } from '../../../_metronic/helpers'
import { InputErrorType } from '../../types/OtherTypes'
import InputErrorBox from '../InputErrorBox'
import Select2 from '../select2'
import { getAttendanceLogs, exportAttendanceLogs } from '../../../api/AttendanceAPI'

interface AttendanceLog {
  id: string
  employeeCode: string
  action: 'CREATE' | 'UPDATE' | 'DELETE'
  actionBy: string
  actionByName?: string
  actionDate: string
  oldValues?: string
  newValues?: string
  details?: string
  recordDate?: string
  inTime?: string
  outTime?: string
}

interface LogFilters {
  action?: 'CREATE' | 'UPDATE' | 'DELETE'
  employeeCode?: string
  startDate?: string
  endDate?: string
  actionBy?: string
  page: number
  limit: number
}

interface AttendanceLogsProps {
  refreshTrigger?: number
}

const AttendanceLogs: React.FC<AttendanceLogsProps> = ({ refreshTrigger }) => {
  const { currentUser } = useAuth()
  const roleState: RoleContextValue = useContext(roleContext)
  const { formattedDate } = getDate()

  // Check if user is System Admin (admin@ctrine.com or admin@ctrine.in) or Super Admin Role
  const isSystemAdmin = currentUser?.email?.toLowerCase() === 'admin@ctrine.com' ||
    currentUser?.email?.toLowerCase() === 'admin@ctrine.in'
  const isSuperAdmin = roleState.roleName === 'Super Admin' || roleState.roleName === 'SUPER_ADMIN'
  const isHrOrRm = currentUser?.isRmOrHr
console.log('currentUser 👉', currentUser)

const hasAccess = isSystemAdmin || isSuperAdmin || isHrOrRm
  //const hasAccess = isSystemAdmin || isSuperAdmin

  const [loading, setLoading] = useState(false)
  const [logs, setLogs] = useState<AttendanceLog[]>([])
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  })
  const [filters, setFilters] = useState<LogFilters>({
    page: 1,
    limit: 10
  })
  const [inputError, setInputError] = useState<InputErrorType>({})
  const [exportLoading, setExportLoading] = useState(false)

  // Load logs
  const loadLogs = async () => {
    setLoading(true)
    try {
      const result = await getAttendanceLogs({
        action: filters.action,
        employeeCode: filters.employeeCode,
        startDate: filters.startDate,
        endDate: filters.endDate,
        actionBy: filters.actionBy,
        page: filters.page,
        limit: filters.limit
      })
 console.log('RM logs raw 👉', result.data?.logs)
      if (result.statusCode === 200) {
        // Sort logs by actionDate in descending order (latest first)
        const sortedLogs = (result.data?.logs || []).sort((a, b) => {
          const dateA = new Date(a.actionDate).getTime()
          const dateB = new Date(b.actionDate).getTime()
          return dateB - dateA // Descending order (latest first)
        })

        setLogs(sortedLogs)
        setPagination(result.data?.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalRecords: 0,
          limit: 10
        })
      } else {
        toast.error(result.message || 'Failed to load logs')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load logs')
    } finally {
      setLoading(false)
    }
  }

  // Load logs on mount, when filters change, or when refreshTrigger changes
  useEffect(() => {
    loadLogs()
  }, [filters.page, filters.limit, refreshTrigger])

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, page: 1 }))
    loadLogs()
  }

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 10
    })
    setInputError({})
    // Reload logs after clearing filters
    setTimeout(() => loadLogs(), 100)
  }

  const handleFilterChange = (e: any) => {
    const { name, value } = e.target
    setFilters(prev => ({ ...prev, [name]: value || undefined }))
  }

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const getActionBadge = (action: string) => {
    const actionConfig: Record<string, { bg: string; color: string; label: string }> = {
      CREATE: { bg: '#F0FDF4', color: '#16A34A', label: 'Added' },
      UPDATE: { bg: '#EAF4FF', color: '#2563EB', label: 'Updated' },
      DELETE: { bg: '#FEF2F2', color: '#DC2626', label: 'Deleted' }
    }
    const config = actionConfig[action] || { bg: '#F1F5F9', color: '#64748B', label: action }
    return (
      <span
        style={{
          backgroundColor: config.bg,
          color: config.color,
          padding: '3px 8px',
          borderRadius: '12px',
          fontSize: '10px',
          fontWeight: '600',
          display: 'inline-block',
          whiteSpace: 'nowrap'
        }}
      >
        {config.label}
      </span>
    )
  }

  const handleExport = async () => {
    setExportLoading(true)
    try {
      const blob = await exportAttendanceLogs({
        action: filters.action,
        employeeCode: filters.employeeCode,
        startDate: filters.startDate,
        endDate: filters.endDate,
        actionBy: filters.actionBy
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const date = new Date().toISOString().split('T')[0]
      link.download = `attendance_logs_${date}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Logs exported successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to export logs')
    } finally {
      setExportLoading(false)
    }
  }

  return (
    <div>
      {!hasAccess ? (
        <div style={{ padding: '50px 24px', textAlign: 'center' }}>
          <div className="text-center">
            <FontAwesomeIcon icon={faLock} className="fa-3x text-danger mb-3" style={{ fontSize: '48px' }} />
            <h5 style={{ marginBottom: '8px' }}>Access Denied</h5>
            <p className="text-muted mb-0" style={{ color: '#64748B' }}>
              Only System Admin (admin@ctrine.com, admin@ctrine.in) and users with Super Admin Role can view AMS Logs
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Filters Section */}
          <div 
            className="card mb-4"
            style={{
              borderRadius: '12px',
              border: '1px solid #E2ECF7',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              backgroundColor: '#FFFFFF'
            }}
          >
            <div 
              className="card-header d-flex justify-content-between align-items-center"
              style={{
                backgroundColor: '#FFFFFF',
                borderBottom: '1px solid #E2ECF7',
                padding: '8px 16px'
              }}
            >
              <h6 
                className="card-title mb-0"
                style={{
                  color: '#123B73',
                  fontWeight: '600',
                  fontSize: '12px'
                }}
              >
                Log Filters
              </h6>
              <small 
                style={{
                  color: '#64748B',
                  fontSize: '10px'
                }}
              >
                {filters.startDate || filters.endDate ?
                  `Showing: ${filters.startDate || 'All'} to ${filters.endDate || 'Today'}` :
                  'Showing all logs'
                }
              </small>
            </div>
            <div className="card-body" style={{ padding: '8px 16px' }}>
              <Row className="align-items-end">
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label 
                      className="fw-bold mb-1"
                      style={{ color: '#123B73', fontSize: '10px', fontWeight: '600' }}
                    >
                      Action
                    </Form.Label>
                    <Form.Select
                      name="action"
                      value={filters.action || ''}
                      onChange={handleFilterChange}
                      style={{
                        borderRadius: '4px',
                        border: '1px solid #E2ECF7',
                        padding: '4px 8px',
                        fontSize: '11px'
                      }}
                    >
                      <option value="">All Actions</option>
                      <option value="CREATE">Added</option>
                      <option value="UPDATE">Updated</option>
                      <option value="DELETE">Deleted</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label 
                      className="fw-bold mb-1"
                      style={{ color: '#123B73', fontSize: '10px', fontWeight: '600' }}
                    >
                      Emp Code
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="employeeCode"
                      value={filters.employeeCode || ''}
                      onChange={handleFilterChange}
                      placeholder="Code..."
                      style={{
                        borderRadius: '4px',
                        border: '1px solid #E2ECF7',
                        padding: '4px 8px',
                        fontSize: '11px'
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label 
                      className="fw-bold mb-1"
                      style={{ color: '#123B73', fontSize: '10px', fontWeight: '600' }}
                    >
                      Start Date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={filters.startDate || ''}
                      onChange={handleFilterChange}
                      style={{
                        borderRadius: '4px',
                        border: '1px solid #E2ECF7',
                        padding: '4px 8px',
                        fontSize: '11px'
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label 
                      className="fw-bold mb-1"
                      style={{ color: '#123B73', fontSize: '10px', fontWeight: '600' }}
                    >
                      End Date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={filters.endDate || ''}
                      onChange={handleFilterChange}
                      style={{
                        borderRadius: '4px',
                        border: '1px solid #E2ECF7',
                        padding: '4px 8px',
                        fontSize: '11px'
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <div className="d-flex gap-1 align-items-end">
                    <button
                      onClick={handleSearch}
                      disabled={loading}
                      style={{
                        backgroundColor: loading ? '#CBD5E1' : '#3B82F6',
                        color: '#FFFFFF',
                        border: 'none',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {loading ? '...' : <FontAwesomeIcon icon={faSearch} style={{ fontSize: '10px' }} />}
                      Search
                    </button>

                    <button
                      onClick={handleClearFilters}
                      style={{
                        backgroundColor: '#FFFFFF',
                        color: '#64748B',
                        border: '1px solid #E2ECF7',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <FontAwesomeIcon icon={faEraser} style={{ fontSize: '10px' }} />
                      Clear
                    </button>

                    <button
                      onClick={() => loadLogs()}
                      disabled={loading}
                      title="Refresh Logs"
                      style={{
                        backgroundColor: '#FFFFFF',
                        color: '#64748B',
                        border: '1px solid #E2ECF7',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <FontAwesomeIcon icon={faArrowRotateRight} style={{ fontSize: '10px' }} />
                      Refresh
                    </button>
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          {/* Logs Table */}
          <div 
            className="card"
            style={{
              borderRadius: '12px',
              border: '1px solid #E2ECF7',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              backgroundColor: '#FFFFFF',
              overflow: 'hidden'
            }}
          >
            <div 
              className="card-header d-flex justify-content-between align-items-center"
              style={{
                backgroundColor: '#FFFFFF',
                borderBottom: '1px solid #E2ECF7',
                padding: '12px 16px'
              }}
            >
              <h5 
                className="card-title mb-0"
                style={{
                  color: '#123B73',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Attendance Logs
                {pagination.totalRecords > 0 && (
                  <span 
                    className="ms-2"
                    style={{
                      color: '#64748B',
                      fontWeight: '400',
                      fontSize: '12px'
                    }}
                  >
                    ({pagination.totalRecords} total)
                  </span>
                )}
              </h5>
            </div>

            <div className="card-body" style={{ padding: '0' }}>
              {loading ? (
                <div style={{ padding: '80px 24px', textAlign: 'center' }}>
                  <div 
                    style={{
                      backgroundColor: '#F4F8FD',
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px'
                    }}
                  >
                    <i 
                      className="fa fa-spinner fa-spin" 
                      style={{ color: '#3B82F6', fontSize: '24px' }}
                    ></i>
                  </div>
                  <div style={{ color: '#64748B', fontSize: '14px' }}>Loading logs...</div>
                </div>
              ) : logs.length > 0 ? (
                <>
                  <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                    <table
                      style={{
                        width: '100%',
                        minWidth: '800px',
                        tableLayout: 'fixed',
                        borderCollapse: 'collapse',
                        backgroundColor: '#FFFFFF'
                      }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: '#F4F8FD' }}>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            color: '#123B73',
                            fontWeight: '600',
                            fontSize: '12px',
                            borderBottom: '1px solid #E2ECF7',
                            whiteSpace: 'nowrap',
                            width: '8%'
                          }}>
                            Action
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            color: '#123B73',
                            fontWeight: '600',
                            fontSize: '12px',
                            borderBottom: '1px solid #E2ECF7',
                            whiteSpace: 'nowrap',
                            width: '12%'
                          }}>
                            Employee Code
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            color: '#123B73',
                            fontWeight: '600',
                            fontSize: '12px',
                            borderBottom: '1px solid #E2ECF7',
                            whiteSpace: 'nowrap',
                            width: '10%'
                          }}>
                            Record Date
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            color: '#123B73',
                            fontWeight: '600',
                            fontSize: '12px',
                            borderBottom: '1px solid #E2ECF7',
                            whiteSpace: 'nowrap',
                            width: '8%'
                          }}>
                            In Time
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            color: '#123B73',
                            fontWeight: '600',
                            fontSize: '12px',
                            borderBottom: '1px solid #E2ECF7',
                            whiteSpace: 'nowrap',
                            width: '8%'
                          }}>
                            Out Time
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            color: '#123B73',
                            fontWeight: '600',
                            fontSize: '12px',
                            borderBottom: '1px solid #E2ECF7',
                            whiteSpace: 'nowrap',
                            width: '18%'
                          }}>
                            Modified On
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            color: '#123B73',
                            fontWeight: '600',
                            fontSize: '12px',
                            borderBottom: '1px solid #E2ECF7',
                            whiteSpace: 'nowrap',
                            width: '36%'
                          }}>
                            Details
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log) => (
                          <tr 
                            key={log.id}
                            style={{
                              borderBottom: '1px solid #E2ECF7',
                              transition: 'background-color 0.2s',
                              backgroundColor: 'transparent'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F8FD'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <td style={{
                              padding: '10px 16px',
                              verticalAlign: 'middle'
                            }}>
                              {getActionBadge(log.action)}
                            </td>
                            <td style={{
                              padding: '10px 16px',
                              color: '#123B73',
                              fontWeight: '600',
                              fontSize: '12px',
                              verticalAlign: 'middle'
                            }}>
                              {log.employeeCode}
                            </td>
                            <td style={{
                              padding: '10px 16px',
                              color: '#64748B',
                              fontSize: '12px',
                              verticalAlign: 'middle'
                            }}>
                              {log.recordDate ? new Date(log.recordDate).toLocaleDateString() : 'N/A'}
                            </td>
                            <td style={{
                              padding: '10px 16px',
                              color: '#64748B',
                              fontSize: '12px',
                              verticalAlign: 'middle'
                            }}>
                              {log.inTime ? log.inTime.substring(0, 5) : 'N/A'}
                            </td>
                            <td style={{
                              padding: '10px 16px',
                              color: '#64748B',
                              fontSize: '12px',
                              verticalAlign: 'middle'
                            }}>
                              {log.outTime ? log.outTime.substring(0, 5) : 'N/A'}
                            </td>
                            <td style={{
                              padding: '10px 16px',
                              color: '#64748B',
                              fontSize: '12px',
                              verticalAlign: 'middle'
                            }}>
                              {new Date(log.actionDate).toLocaleString()}
                            </td>
                            <td style={{
                              padding: '10px 16px',
                              color: '#64748B',
                              fontSize: '12px',
                              verticalAlign: 'middle'
                            }}>
                              <small>{log.details || '-'}</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {logs.length > 0 && (
                    <div
                      className="d-flex justify-content-between align-items-center"
                      style={{
                        padding: '10px 16px',
                        borderTop: '1px solid #E2ECF7',
                        backgroundColor: '#FFFFFF'
                      }}
                    >
                      <span style={{ color: '#64748B', fontSize: '11px' }}>
                        {(() => {
                          const total = pagination.totalRecords
                          if (total === 0) {
                            return '0 - 0 of 0'
                          }
                          const start = (pagination.currentPage - 1) * pagination.limit + 1
                          const end = Math.min(pagination.currentPage * pagination.limit, total)
                          return `${start} - ${end} of ${total}`
                        })()}
                      </span>
                      {pagination.totalPages > 1 && (
                        <div className="d-flex gap-1">
                          <button
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                            style={{
                              backgroundColor: pagination.currentPage === 1 ? '#F1F5F9' : '#FFFFFF',
                              color: pagination.currentPage === 1 ? '#CBD5E1' : '#64748B',
                              border: '1px solid #E2ECF7',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: pagination.currentPage === 1 ? 'not-allowed' : 'pointer',
                              fontSize: '10px',
                              transition: 'all 0.2s'
                            }}
                          >
                            Prev
                          </button>
                          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                            let pageNum;
                            if (pagination.totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (pagination.currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (pagination.currentPage >= pagination.totalPages - 2) {
                              pageNum = pagination.totalPages - 4 + i;
                            } else {
                              pageNum = pagination.currentPage - 2 + i;
                            }
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  backgroundColor: pagination.currentPage === pageNum ? '#3B82F6' : '#FFFFFF',
                                  color: pagination.currentPage === pageNum ? '#FFFFFF' : '#64748B',
                                  border: '1px solid #E2ECF7',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '10px',
                                  transition: 'all 0.2s',
                                  minWidth: '24px'
                                }}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                            style={{
                              backgroundColor: pagination.currentPage === pagination.totalPages ? '#F1F5F9' : '#FFFFFF',
                              color: pagination.currentPage === pagination.totalPages ? '#CBD5E1' : '#64748B',
                              border: '1px solid #E2ECF7',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: pagination.currentPage === pagination.totalPages ? 'not-allowed' : 'pointer',
                              fontSize: '10px',
                              transition: 'all 0.2s'
                            }}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div 
                  style={{
                    padding: '80px 24px',
                    textAlign: 'center',
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  <div 
                    style={{
                      backgroundColor: '#F4F8FD',
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px'
                    }}
                  >
                    <i 
                      className="fa fa-file-alt fa-2x" 
                      style={{ color: '#64748B', fontSize: '32px' }}
                    ></i>
                  </div>
                  <div 
                    style={{ 
                      color: '#123B73', 
                      fontWeight: '600', 
                      marginBottom: '8px',
                      fontSize: '18px'
                    }}
                  >
                    No logs found
                  </div>
                  <div 
                    style={{ 
                      color: '#64748B',
                      fontSize: '14px'
                    }}
                  >
                    Try adjusting your filters
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default AttendanceLogs
